import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  exhaustMap,
  filter,
  finalize,
  map,
  scan,
  shareReplay,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import { TrackByFunction } from '@angular/core';
import { valueToSnake } from '@app/core/utilities/string.utilities';
import { lastValue } from '@app/shared/operators/last-value.operator';
import {
  API_DEFAULT_LIMIT,
  API_DEFAULT_OFFSET,
  API_MAX_LIMIT,
  ApiGetListRequest,
  ApiGetListResponse,
  ApiListMutatorSettings,
  ApiPaginationResponse,
} from '../models/api.models';
import { ApiGetMutator } from './api-get-mutator.class';

export class ApiListMutator<T extends object> extends ApiGetMutator<T, ApiGetListRequest<T>, ApiGetListResponse<T>> {
  private readonly nextPage = new BehaviorSubject(void 0);

  private readonly loadingMore = new BehaviorSubject(false);

  private readonly limit = new BehaviorSubject<number>(1);

  private readonly offset = new BehaviorSubject<number>(1);

  readonly source: Observable<ApiGetListResponse<T>> = this.inputs.pipe(
    tap(() => this.resetLimitOffset()),
    switchMap(
      ([
        [include, exclude, groupBy],
        [searchTerm, searchFields],
        [sortValues, searchValues, filterValues, fixedFilterValues],
      ]) =>
        this.refresh$.pipe(
          switchMap(() =>
            this.nextPage.pipe(
              tap(() => this.loading.next(true)),
              withLatestFrom(combineLatest([this.limit, this.offset])),
              exhaustMap(([_, [limit, offset]]) =>
                this.api({
                  include,
                  exclude,
                  groupBy,
                  searchTerm,
                  searchFields,
                  sortValues,
                  searchValues,
                  filterValues,
                  fixedFilterValues,
                  limit,
                  offset,
                  accessLevel: this.settings.accessLevel,
                  impersonationId: this.settings.impersonationId,
                  disablePaging: this.settings.disablePaging,
                  ...(this.init.endpoint ? { endpoint: this.init.endpoint } : {}),
                  ...(this.init.route ? { route: this.init.route } : {}),
                  ...(this.init.params ? { params: valueToSnake(this.init.params) } : {}),
                }).pipe(
                  switchMap((response) => {
                    if (this.settings.listMutator) {
                      const mutatedList = this.settings.listMutator(response.data);

                      if (Array.isArray(mutatedList)) {
                        return of({ ...response, data: mutatedList });
                      } else {
                        return mutatedList.pipe(map((data) => ({ ...response, data })));
                      }
                    } else {
                      return of(response);
                    }
                  }),
                  finalize(() => {
                    this.loading.next(false);
                    this.loadingMore.next(false);
                  }),
                  catchError((e) =>
                    of({ ...e, data: [], meta: { pagination: { offset, limit, total: 0 } } } as ApiGetListResponse<T>),
                  ),
                ),
              ),
              scan(
                (a: ApiGetListResponse<T> | undefined, b: ApiGetListResponse<T>) => {
                  if (!a) {
                    return b;
                  } else {
                    a.data = [...a.data, ...b.data];

                    return a;
                  }
                },
                void 0,
              ),
            ),
          ),
        ),
    ),
    tap(() => this.firstLoad.next(false)),
    shareReplay({ bufferSize: 1, refCount: !this.settings.persistent }),
  );

  readonly list$: Observable<T[]> = this.source.pipe(map(({ data }) => data || []));

  readonly pagination$: Observable<ApiPaginationResponse<T>> = this.source.pipe(
    map(({ meta }) => meta.pagination || ({} as ApiPaginationResponse<T>)),
  );

  readonly total$: Observable<number> = this.source.pipe(
    map(({ meta, data }) => (meta.pagination && meta.pagination.total) || (data && data.length) || 0),
  );

  readonly noData$: Observable<boolean> = this.total$.pipe(map((total) => !total));

  readonly loadingMore$: Observable<boolean> = this.loadingMore.asObservable().pipe(debounceTime(1));

  readonly canLoadMore$: Observable<boolean> = this.source.pipe(
    map(({ data, meta }) => data.length < ((meta.pagination && meta.pagination.total) || 0)),
  );

  readonly trackById: TrackByFunction<T> = (idx: number, item: T) =>
    this.settings.trackById ? item?.[this.settings.trackById] : idx;

  constructor(
    listApi: (req: Partial<ApiGetListRequest<T>>) => Observable<ApiGetListResponse<T>>,
    init: Partial<ApiGetListRequest<T>> = {},
    protected settings: ApiListMutatorSettings<T> = {},
  ) {
    super(listApi, init, settings);
    this.reset();
  }

  refresh(): Observable<void> {
    const currentOffset = this.offset.getValue();
    const currentLimit = this.limit.getValue();
    this.limit.next(currentOffset + currentLimit);
    this.resetOffset();

    return super.refresh();
  }

  reset(): void {
    super.reset(true);
    this.resetLimitOffset();
  }

  loadMore(): Observable<boolean> {
    if (!this.loading.getValue() && lastValue(this.canLoadMore$)) {
      const previousLimit = this.limit.getValue();
      this.resetLimit();
      const currentOffset = this.offset.getValue();

      this.offset.next(currentOffset + previousLimit);
      this.nextPage.next(void 0);
      this.loadingMore.next(true);

      return this.loadingMore.pipe(
        filter((loadingMore) => !loadingMore),
        take(1),
        map(() => true),
      );
    } else {
      return of(false);
    }
  }

  loadAll(): Observable<void> {
    if (lastValue(this.canLoadMore$)) {
      this.limit.next(API_MAX_LIMIT);
      this.nextPage.next(void 0);
      this.loadingMore.next(true);

      return this.loadingMore.pipe(
        filter((loadingMore) => !loadingMore),
        take(1),
        switchMap(() => this.loadAll()),
      );
    } else {
      return of(void 0);
    }
  }

  setPage(pageIndex: number): void {
    const limit = lastValue(this.limit);
    this.offset.next(pageIndex * limit);
    this.refresh$.next();
  }

  setLimit(limit: number): void {
    this.limit.next(limit);
    this.offset.next(this.init.offset);
    this.refresh$.next();
  }

  private resetLimitOffset(): void {
    this.resetLimit();
    this.resetOffset();
  }

  private resetLimit(): void {
    this.limit.next(this.init.limit || API_DEFAULT_LIMIT);
  }

  private resetOffset(): void {
    this.offset.next(this.init.offset || API_DEFAULT_OFFSET);
  }
}
